import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, PencilSimple, Trash, MagnifyingGlass, AddressBook, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CLIENT_TYPES, CLIENT_CATEGORIES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const EMPTY = { name: "", client_type: "Individual", category: "", contact_person: "", phone: "", email: "", address: "", gstin: "", notes: "", city: "", group_name: "", kyc_status: "pending" };

const CAT_COLORS = {
  "Bank Initiated": "bg-blue-50 text-blue-700 border-blue-200",
  "CA Initiated": "bg-amber-50 text-amber-700 border-amber-200",
  "Self Initiated": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Clients() {
  const [params] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("new")) { setEditing(null); setForm(EMPTY); setOpen(true); } }, [params]);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ ...EMPTY, ...c }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/clients/${editing}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? "Client updated" : "Client added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/clients/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = clients.filter((c) => {
    const q = search.toLowerCase();
    const matchQ = !q || `${c.name} ${c.contact_person} ${c.phone} ${c.email} ${c.city || ""} ${c.group_name || ""}`.toLowerCase().includes(q);
    const matchT = typeFilter === "all" || c.client_type === typeFilter;
    const matchC = cityFilter === "all" || (c.city || "Unspecified") === cityFilter;
    return matchQ && matchT && matchC;
  });

  const cities = [...new Set(clients.map((c) => c.city || "Unspecified"))].sort();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">CRM</div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">{clients.length} clients · individuals, builders & companies you bill</p>
        </div>
        <Button onClick={openNew} data-testid="add-client-btn" className="rounded-full bg-primary hover:bg-[#113A64]"><Plus className="mr-1" size={16} /> Add Client</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, contact, phone…" className="pl-9" data-testid="client-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[190px]" data-testid="client-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem>{CLIENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={cityFilter} onValueChange={setCityFilter}>
          <SelectTrigger className="w-full sm:w-[170px]" data-testid="client-city-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Cities</SelectItem>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Card key={c.id} className="p-5 border-border transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`client-card-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><AddressBook size={18} weight="duotone" className="text-primary shrink-0" /><h3 className="font-display font-semibold text-lg text-primary truncate">{c.name}</h3></div>
                <div className="flex items-center flex-wrap gap-2 mt-0.5">
                  <span className="text-xs text-accent">{c.client_type}</span>
                  {c.category && <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${CAT_COLORS[c.category] || "bg-muted text-muted-foreground border-border"}`} data-testid={`client-category-badge-${c.id}`}>{c.category}</span>}
                </div>
              </div>
            </div>
            <div className="mt-3 text-sm space-y-0.5">
              {c.contact_person && <div className="text-primary">{c.contact_person}</div>}
              {c.phone && <div className="text-muted-foreground font-mono text-xs">{c.phone}</div>}
              {c.email && <div className="text-muted-foreground text-xs truncate">{c.email}</div>}
              {c.gstin && <div className="text-muted-foreground text-xs">GSTIN: {c.gstin}</div>}
            </div>
            <div className="mt-4 flex items-center gap-2 justify-end">
              <Button asChild size="icon" variant="outline" className="h-8 w-8" title="Open profile">
                <Link to={`/app/clients/${c.id}`} data-testid={`open-client-${c.id}`}><ArrowRight size={15} /></Link>
              </Button>
              <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}><PencilSimple size={15} /></Button>
              <AlertDialog>
                <AlertDialogTrigger asChild><Button size="icon" variant="outline" className="h-8 w-8 text-destructive" data-testid={`delete-client-${c.id}`}><Trash size={15} /></Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete client?</AlertDialogTitle><AlertDialogDescription>Remove {c.name}? This can't be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(c.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground">No clients yet. Add your first client.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Client" : "Add Client"}</DialogTitle><DialogDescription>Store client contact and billing details.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="client-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name *</Label><Input required value={form.name} onChange={set("name")} data-testid="client-name" /></div>
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={form.client_type} onValueChange={set("client_type")}><SelectTrigger data-testid="client-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLIENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact Person</Label><Input value={form.contact_person} onChange={set("contact_person")} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={set("email")} /></div>
              <div className="space-y-1.5"><Label>GSTIN</Label><Input value={form.gstin} onChange={set("gstin")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>City</Label><Input value={form.city} onChange={set("city")} data-testid="client-city" /></div>
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={form.category || ""} onValueChange={set("category")}><SelectTrigger data-testid="client-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{CLIENT_CATEGORIES.map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Client Group</Label><Input value={form.group_name} onChange={set("group_name")} placeholder="e.g. Agrawal Family" data-testid="client-group" /></div>
            <div className="space-y-1.5"><Label>Address</Label><Textarea value={form.address} onChange={set("address")} rows={2} /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={set("notes")} rows={2} /></div>
            <DialogFooter><Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-client-btn">{editing ? "Update" : "Add"} Client</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
